import {createSelector} from 'reselect'

import {lbToOz, combineToLb} from '../../../common/weight.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import WeightInput from '../../../common/components/Form/WeightInput.js'
import {addProducts, saveProduct, nameSelector} from '../../../data/products.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {formsSelector} from '../../../redux/selectors/ui/forms.js'
import Checkbox from '../../../common/components/Checkbox.js'
import {hasOrderPermissionSelector} from '../../../data/me.js'
import {
  getState,
  onlyIfForm,
  removeForm,
  setForm,
  updateForm,
  useSelector,
} from '../../../store.js'

const MODAL_FORM = 'product_weight_modal'

export function showProductWeightModal(skus, weight = 0) {
  setForm(MODAL_FORM, {
    skus,
    oz: lbToOz(weight),
    hasInputError: false,
    refresh_label_info_package: false,
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export const modalFormSelector = createSelector(
  formsSelector,
  (forms) => forms[MODAL_FORM],
)

export async function saveProductWeight() {
  try {
    const {skus, lb, oz, refresh_label_info_package} =
      modalFormSelector(getState())
    const params = {
      weight: combineToLb(lb, oz),
      refresh_label_info_package,
      refresh_label_info_customs: refresh_label_info_package,
    }

    updateModalForm({isSaving: true, serverError: null})

    const products = await Promise.all(
      skus.map((sku) => saveProduct(sku, params)),
    )

    addProducts(products)

    closeModal()

    showMessageToast('Saved product weight')
  } catch (err) {
    updateModalForm({serverError: err.message || err.error_message})
  } finally {
    updateModalForm({isSaving: false})
  }
}

function ProductWeightModal({form}) {
  const firstSKU = form.skus[0]
  const firstProductName = useSelector((state) =>
    nameSelector(state, {sku: firstSKU}),
  )
  const hasOrderPermission = useSelector(hasOrderPermissionSelector)

  return (
    <ConfirmModal
      title="Edit Weight"
      modalSize="x-sm"
      onConfirm={() => saveProductWeight()}
      onCancel={() => closeModal()}
      confirmText="Save"
      cancelText="Cancel"
      isSaving={form.isSaving}
      isDisabled={form.hasInputError}
      error={form.serverError}
    >
      <ul className="list">
        <li className="list__item--form list__item--no-style margin-bottom-20">
          {form.skus.length === 1 && (
            <>
              <div className="fs-01">
                <strong>{firstProductName}</strong>
              </div>
              <div className="fs-00">{firstSKU}</div>
            </>
          )}
          {form.skus.length > 1 && (
            <strong className="fs-01">
              Editing {form.skus.length} products
            </strong>
          )}
        </li>
        <li className="list__item--form list__item--no-style flex">
          <WeightInput
            weightOz={form.oz}
            setWeightOz={(weightOz) => updateModalForm({oz: weightOz})}
            onEnterKeyPress={() => saveProductWeight()}
            onErrorChange={(err) =>
              updateModalForm({
                hasInputError: err.lb || err.oz,
              })
            }
            showScaleControl
            autoFocus
          />
        </li>
        {hasOrderPermission && (
          <li className="list__item--form list__item--no-style divider--top">
            <p className="fs-n0 lh-md margin-bottom-10">
              Want to update the Awaiting Fulfillment and Dropshipment Requested
              orders this product is on when a change is made to its weight?
            </p>
            <Checkbox
              id="refresh_label_info_customs"
              className="fs-00 lh-md"
              label="Update open orders"
              checked={!!form.refresh_label_info_package}
              onChange={(refresh_label_info_package) =>
                updateModalForm({refresh_label_info_package})
              }
            />
          </li>
        )}
      </ul>
    </ConfirmModal>
  )
}

export default onlyIfForm(ProductWeightModal, modalFormSelector)
